/**
 * UI settings: language, display toggles, filters, collapsed groups and which
 * profile is active. Persisted to localStorage independently of progress, so a
 * future cloud adapter has nothing to say about them.
 */

import { create } from 'zustand';
import type { Lang, Rarity } from '@/domain/content.types';
import { detectLang } from '@/i18n';

const KEY = 'meow:v1:settings';

export type StatusFilter = 'all' | 'done' | 'todo';
export type ViewMode = 'list' | 'map';

export interface FilterState {
  search: string;
  status: StatusFilter;
  rarities: Rarity[];
}

export const DEFAULT_FILTERS: FilterState = { search: '', status: 'all', rarities: [] };

interface PersistedSettings {
  lang: Lang;
  activeProfileId: string | null;
  activeSetId: string | null;
  showBothNames: boolean;
  scanlines: boolean;
  /**
   * Explicit choice per set. Absent means "use the default", which is the map
   * for a set that has one — see `viewFor`. Per set because the answer differs
   * per set: a list of weekly rewards is a list, a hunt for 108 masks is a map.
   */
  views: Record<string, ViewMode>;
  collapsed: Record<string, boolean>;
}

/**
 * Which view a set opens in. A mapped set opens on its map, because that is
 * what it is for; anything else opens as a list. Either way the user's own
 * toggle wins and is remembered for that set alone.
 */
export function viewFor(
  views: Record<string, ViewMode>,
  setId: string,
  hasMap: boolean,
): ViewMode {
  const chosen = views[setId];
  if (chosen === 'map') return hasMap ? 'map' : 'list';
  if (chosen === 'list') return 'list';
  return hasMap ? 'map' : 'list';
}

interface SettingsState extends PersistedSettings {
  filters: FilterState;
  setLang: (lang: Lang) => void;
  toggleLang: () => void;
  setActiveProfileId: (id: string | null) => void;
  setActiveSetId: (id: string) => void;
  setShowBothNames: (value: boolean) => void;
  setScanlines: (value: boolean) => void;
  setView: (setId: string, view: ViewMode) => void;
  /**
   * Explicit user choice only. Absent means "use the default", which the
   * group computes (content flag, or a phase that has ended) — so a week that
   * ends tonight folds itself tomorrow unless the user has said otherwise.
   */
  setCollapsed: (setId: string, groupId: string, collapsed: boolean) => void;
  setFilters: (patch: Partial<FilterState>) => void;
  resetFilters: () => void;
  filtersAreDefault: () => boolean;
}

/** Keeps only real view modes, so a hand-edited or outdated payload cannot
 *  leave the app rendering nothing. The pre-set-aware global `view` key is
 *  dropped on purpose: the new defaults land in the same place for every set
 *  that had one. */
function sanitizeViews(raw: unknown): Record<string, ViewMode> {
  if (raw === null || typeof raw !== 'object') return {};
  const out: Record<string, ViewMode> = {};
  for (const [id, mode] of Object.entries(raw as Record<string, unknown>)) {
    if (mode === 'list' || mode === 'map') out[id] = mode;
  }
  return out;
}

function load(): PersistedSettings {
  const base: PersistedSettings = {
    lang: detectLang(null),
    activeProfileId: null,
    activeSetId: null,
    showBothNames: false,
    scanlines: true,
    views: {},
    collapsed: {},
  };
  try {
    const raw = globalThis.localStorage?.getItem(KEY);
    if (!raw) return base;
    const parsed = JSON.parse(raw) as Partial<PersistedSettings>;
    return {
      ...base,
      ...parsed,
      lang: detectLang(parsed.lang ?? null),
      views: sanitizeViews(parsed.views),
      collapsed: parsed.collapsed ?? {},
    };
  } catch {
    return base;
  }
}

function persist(state: SettingsState): void {
  const payload: PersistedSettings = {
    lang: state.lang,
    activeProfileId: state.activeProfileId,
    activeSetId: state.activeSetId,
    showBothNames: state.showBothNames,
    scanlines: state.scanlines,
    views: state.views,
    collapsed: state.collapsed,
  };
  try {
    globalThis.localStorage?.setItem(KEY, JSON.stringify(payload));
  } catch {
    /* private mode / blocked storage — settings simply do not persist */
  }
}

/** Applies language to the document so screen readers and hyphenation follow. */
function syncDocumentLang(lang: Lang): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lang;
}

export const useSettings = create<SettingsState>((set, get) => {
  const initial = load();
  syncDocumentLang(initial.lang);

  const save = (): void => persist(get());

  return {
    ...initial,
    filters: { ...DEFAULT_FILTERS },

    setLang: (lang) => {
      set({ lang });
      syncDocumentLang(lang);
      save();
    },
    toggleLang: () => get().setLang(get().lang === 'en' ? 'ru' : 'en'),

    setActiveProfileId: (activeProfileId) => {
      set({ activeProfileId });
      save();
    },
    setActiveSetId: (activeSetId) => {
      set({ activeSetId });
      save();
    },
    setShowBothNames: (showBothNames) => {
      set({ showBothNames });
      save();
    },
    setScanlines: (scanlines) => {
      set({ scanlines });
      save();
    },
    setView: (setId, view) => {
      set({ views: { ...get().views, [setId]: view } });
      save();
    },

    setCollapsed: (setId, groupId, value) => {
      set({ collapsed: { ...get().collapsed, [`${setId}/${groupId}`]: value } });
      save();
    },

    setFilters: (patch) => set({ filters: { ...get().filters, ...patch } }),
    resetFilters: () => set({ filters: { ...DEFAULT_FILTERS } }),
    filtersAreDefault: () => {
      const f = get().filters;
      return f.search.trim() === '' && f.status === 'all' && f.rarities.length === 0;
    },
  };
});
